/**
 * Text-only multipart/form-data bodies, composed as an exact string.
 *
 * The backend verifies `Access-Signature` against `{user_id}:{access_time}:{body}`,
 * where `body` is the raw request bytes decoded as UTF-8
 * (b-cell-auth `validator/authentication/post.py`). A signature therefore only
 * verifies if the caller signs the byte-for-byte body it is about to send.
 *
 * `FormData` cannot be used for this: the browser picks the boundary and serializes
 * the parts itself, so the exact bytes are never available to sign. Composing the
 * body here instead — and sending that same string with a `Content-Type` naming the
 * boundary we chose — is what keeps the signed message and the transmitted body
 * identical.
 *
 * Text only, deliberately. A binary part cannot survive the UTF-8 decode the
 * verifier performs, so binary uploads cannot be signed at all until that is
 * addressed backend-side; `assertSignableText` is what keeps a file that would
 * silently corrupt out of the request.
 */

import { bytesToHex } from './hex';

const CRLF = '\r\n';

/** U+FFFD: what `Blob.text()` substitutes for bytes that are not valid UTF-8. */
const REPLACEMENT_CHARACTER = '\uFFFD';

export interface MultipartPart {
  /** The part's `name` attribute — the key the backend validator looks the part up by. */
  name: string;
  /** The part's content. Must be text; see the module note. */
  value: string;
  /** Present only on file parts; becomes the attachment's `file_name`. */
  fileName?: string;
  /** Present only on file parts. */
  contentType?: string;
}

export interface MultipartBody {
  /** The exact string to sign and to send as the request body. */
  body: string;
  boundary: string;
  /** The `Content-Type` header value naming `boundary`. */
  contentType: string;
}

/**
 * A boundary that does not occur anywhere in the parts.
 *
 * A boundary that appeared inside a part's content would split the body at the wrong
 * place, so the candidate is checked against the content rather than assumed unique.
 * A 32-hex-character random string effectively never collides; the loop is what makes
 * that "effectively" unnecessary.
 */
function uniqueBoundary(parts: MultipartPart[]): string {
  for (let attempt = 0; attempt < 8; attempt += 1) {
    const random = new Uint8Array(16);
    crypto.getRandomValues(random);
    const candidate = `veltiz${bytesToHex(random)}`;
    if (!parts.some((part) => part.value.includes(candidate))) return candidate;
  }
  throw new Error('Could not compose a unique multipart boundary');
}

/**
 * A file name safe to place inside a quoted header parameter.
 *
 * Quotes and newlines would end the parameter (or the header) early, letting a chosen
 * file name rewrite the part headers. The backend sanitizes `file_name` again on its
 * side and treats it as display-only, so this guard is about the request staying
 * well-formed, not about what gets stored.
 */
function headerSafeFileName(fileName: string): string {
  return fileName.replace(/[\r\n"\\]/g, '_');
}

/** Composes the parts into one `multipart/form-data` body string. */
export function buildMultipartBody(parts: MultipartPart[]): MultipartBody {
  const boundary = uniqueBoundary(parts);

  const serializedParts = parts.map((part) => {
    const disposition = part.fileName === undefined
      ? `Content-Disposition: form-data; name="${part.name}"`
      : `Content-Disposition: form-data; name="${part.name}"; filename="${headerSafeFileName(part.fileName)}"`;
    const headers = part.contentType === undefined
      ? disposition
      : `${disposition}${CRLF}Content-Type: ${part.contentType}`;
    return `--${boundary}${CRLF}${headers}${CRLF}${CRLF}${part.value}${CRLF}`;
  });

  return {
    body: `${serializedParts.join('')}--${boundary}--${CRLF}`,
    boundary,
    contentType: `multipart/form-data; boundary=${boundary}`,
  };
}

/**
 * Throws unless `text` survives a UTF-8 round trip unchanged.
 *
 * `Blob.text()` always decodes as UTF-8 and substitutes U+FFFD for anything that is
 * not — so a Latin-1 or binary file reaches us already lossy. Sending it anyway would
 * still *verify* (we sign what we send), which is exactly the danger: the upload would
 * succeed while storing corrupted content. Refusing here keeps a decode failure a
 * visible error instead of silent data loss.
 */
export function assertSignableText(text: string, message: string): void {
  if (text.includes(REPLACEMENT_CHARACTER)) throw new Error(message);
}
